import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  Truck,
  MapPin,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Plus,
} from "lucide-react";

export default function MyTrips() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/logistics/trips/by-driver/summary"),
          api.get("/logistics/trips/by-driver", { params: { limit: 50 } }),
        ]);
        setSummary(s.data);
        setRows(r.data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="my-trips-page">
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Total Trips</div>
          <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Chivo" }}>
            {summary?.total_trips ?? "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Open / Pending</div>
          <div className="text-2xl font-bold mt-1 text-bravura-300" style={{ fontFamily: "Chivo" }}>
            {summary?.open_trips ?? "—"}
          </div>
        </div>
      </div>

      <Link
        to="/driver/trips/new"
        data-testid="log-new-trip"
        className="block rounded-2xl bg-bravura text-white p-4 shadow-md active:scale-[0.99] transition"
      >
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-slate-900 text-bravura-300 flex items-center justify-center">
            <Plus className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="text-base font-bold">Log a New Trip</div>
            <div className="text-xs text-slate-700">Truck + destination + expenses</div>
          </div>
          <ChevronRight className="h-5 w-5" />
        </div>
      </Link>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 text-center text-sm text-slate-500">
          No trips logged yet.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((t) => (
          <Link
            key={t.id}
            to={`/driver/trips/${t.id}`}
            data-testid={`trip-row-${t.id}`}
            className="block rounded-xl bg-white ring-1 ring-slate-200 p-3 active:scale-[0.99] transition"
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                <Truck className="h-5 w-5 text-sky-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-bold text-slate-900 truncate">{t.truck_no}</div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    #{t.trip_seq}
                  </span>
                  {t.status === "settled" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /> Settled
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-bravura-100 text-bravura-700">
                      <AlertCircle className="h-3 w-3" /> Open
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3 flex-shrink-0" /> {t.location_name}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {t.date} · Expense: <b>{formatINR(t.actual_total)}</b>
                  {t.variance_flagged && (
                    <span className="ml-1.5 text-red-600 font-semibold">⚠ variance</span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
